// English messages for the documents part of the interface. Spread into en.ts.
// Same rules as en.ts: strings that existed before translation are kept word
// for word, so the screen tests (which run in English) describe the same screen.

import type { Message, Vars } from './en.ts';

const n = (vars: Vars, name = 'count'): number => Number(vars[name] ?? 0);

export const documentsEn = {
  'doc.channel.upload': 'upload',
  'doc.channel.mailbox': 'mailbox',
  // --- the screen and the inbox ----------------------------------------------
  'doc.title': 'Documents',
  'doc.subtitle': 'From receipt to confirmed booking. Every source stays traceable.',
  'doc.replay.button': 'Watch it read again',
  'doc.inbox.subtitle': 'Documents that still need something',
  'doc.inbox.error': 'The document inbox could not be read.',
  'doc.inbox.emptyTitle': 'Nothing waiting',
  'doc.inbox.emptyText': 'No open documents in this selection.',
  'doc.kind.photo': 'Photo',
  'doc.loading': 'Loading…',
  'doc.select.title': 'Select a document',
  'doc.select.text': 'Its source, the prepared proposal and everything decided about it appear here.',

  // --- the document view -----------------------------------------------------
  'doc.error.absent': 'This document is not available to your session.',
  'doc.error.read': 'The document could not be read.',
  'doc.loadingDocument': 'Loading document…',
  'doc.header.received': (v: Vars) => `received ${v['when']}`,
  'doc.header.pages': (v: Vars) => `${n(v)} ${n(v) === 1 ? 'page' : 'pages'}`,
  'doc.size.bytes': (v: Vars) => `${v['size']} bytes`,
  'doc.size.kb': (v: Vars) => `${v['size']} KB`,
  'doc.size.mb': (v: Vars) => `${v['size']} MB`,
  'doc.header.regionWaiting': 'Waiting for the region report',
  'doc.header.reading': 'Your AI is reading',
  // The document type as stored. English shows the stored value itself; a
  // type with no name here, or none at all ("Document"), is shown as is.
  'doc.type.invoice': 'invoice',
  'doc.type.credit_note': 'credit_note',
  'doc.type.receipt': 'receipt',
  'doc.direction.inbound': 'inbound',
  'doc.direction.outbound': 'outbound',

  // --- the replay ------------------------------------------------------------
  'doc.replay.title': 'Replay of how this was read',
  'doc.replay.stored': 'Played back from the stored record.',
  'doc.replay.took': (v: Vars) =>
    `Played back from the stored record. The reading itself took ${v['took']}, on ${v['when']}.`,
  'doc.replay.under': 'under 0,1 s',

  // --- callouts --------------------------------------------------------------
  'doc.statement.title': 'Bank statement',
  'doc.blockers.title': 'Stopped before preparation could finish',
  'doc.warnings.title': 'Check before approving',
  'doc.vat.didYouMean': (v: Vars) => `Did you mean ${v['candidate']}?`,
  'doc.vat.explain': (v: Vars) =>
    `Read as ${v['read']}: the letter in the twelfth position should be a B. With it, the number is exactly that of ${v['relation']}${v['code']} in e-Boekhouden. Nothing changes unless you confirm it; your confirmation is recorded.`,
  'doc.vat.anyRelation': 'an existing relation',
  'doc.vat.use': (v: Vars) => `Use ${v['candidate']}`,
  'doc.payment.title': 'How was this receipt paid?',
  'doc.payment.text': 'The receipt does not say. It is booked from where the money left; your answer is recorded.',
  'doc.payment.cash': 'Cash (Kas)',
  'doc.payment.bank': 'Card or bank',
  'doc.payment.submit': 'Record and read again',
  'doc.settlement.state': (v: Vars) => `Settlement against the original invoice: ${v['state']}.`,
  'doc.settlement.offerTitle': (v: Vars) => `Set off against invoice ${v['number']}`,
  'doc.settlement.offerText': (v: Vars) =>
    `${v['amount']} of this credit note against the original invoice in e-Boekhouden. No money moves; the credit note is cleared and the invoice reduced. A separate decision.`,
  'doc.settlement.approve': 'Approve settlement',
  'doc.settlement.not_booked': 'Settlement against the original is offered once this credit note is booked.',
  'doc.settlement.no_original': 'The credit note names no original invoice, so there is nothing to set it off against here.',
  'doc.settlement.not_released': "Settling credit notes can't be done in e-Boekhouden from here yet.",
  'doc.settlement.ledger_not_configured':
    'No settlement ledger is set for this administration, so the settlement cannot be approved yet. Ask an operator to set it.',
  'doc.region.pendingTitle': 'Waiting for the provider to report where this was processed',
  'doc.region.neverTitle': 'The region was never reported',
  'doc.region.held': 'The model has answered, but the answer is held and not used until OpenRouter confirms it ran in the EU.',
  'doc.region.asked': (v: Vars) => `Asked ${n(v)} ${n(v) === 1 ? 'time' : 'times'}, last at ${v['at']}.`,
  'doc.region.next': (v: Vars) =>
    `Next check ${v['next']}; a person takes over at ${v['deadline']} if it never comes.`,
  'doc.region.askOnce': 'You can ask once more. Nothing is sent again.',
  'doc.region.button': 'Check region again',

  // --- the checks ------------------------------------------------------------
  'doc.checks.title': 'Checks',
  'doc.check.vat_arithmetic': 'VAT arithmetic',
  'doc.check.vat_rate_code': 'VAT rate matches its code',
  'doc.check.supplier_mapped': 'Supplier mapped',
  'doc.check.not_duplicate': 'Not a duplicate',
  'doc.check.region_confirmed': 'Region confirmed',
  'doc.check.outcome.passed': 'passed',
  'doc.check.outcome.failed': 'failed',
  'doc.check.outcome.not_run': 'not run yet',
  'doc.check.beforeSending': 'checked before sending',
  'doc.check.when.fields': 'Runs once every required field has been read.',
  'doc.check.when.duplicate': 'Runs at the accounting package when the approved booking is sent.',
  'doc.check.when.region': 'Runs when the text is sent to the model; this reading made no lookup.',

  // --- the booking proposal --------------------------------------------------
  'doc.proposal.none': 'No proposal has been prepared for this document.',
  'doc.proposal.title': 'Booking proposal',
  'doc.proposal.revision': (v: Vars) => `Revision ${v['revision']}`,
  'doc.proposal.booksAs': 'Books as',
  'doc.proposal.ledgers': 'Ledgers',
  'doc.proposal.revenueLedger': 'Revenue ledger',
  'doc.proposal.costLedger': 'Cost ledger',
  'doc.proposal.paidFrom': 'Paid from',
  'doc.proposal.vatCode': 'VAT code',
  'doc.proposal.supplier': 'Supplier',
  'doc.proposal.total': 'Total',
  'doc.proposal.vatNotReclaimed': (v: Vars) => `VAT not reclaimed: ${v['amount']}.`,
  'doc.proposal.vatNotReclaimedWhy':
    "This supplier's VAT is not deductible (its rule says so), so the gross amount is booked to the cost ledger under the no-VAT code.",
  'doc.booksAs.purchase.invoice': 'Purchase invoice',
  'doc.booksAs.purchase.credit_note': 'Purchase credit note',
  'doc.booksAs.purchase.receipt': 'Purchase receipt',
  'doc.booksAs.purchase.document': 'Purchase document',
  'doc.booksAs.purchase.other': (v: Vars) => `Purchase ${v['kind']}`,
  'doc.booksAs.sales.invoice': 'Sales invoice',
  'doc.booksAs.sales.credit_note': 'Sales credit note',
  'doc.booksAs.sales.receipt': 'Sales receipt',
  'doc.booksAs.sales.document': 'Sales document',
  'doc.booksAs.sales.other': (v: Vars) => `Sales ${v['kind']}`,
  'doc.booksAs.none.invoice': 'Invoice',
  'doc.booksAs.none.credit_note': 'Credit note',
  'doc.booksAs.none.receipt': 'Receipt',
  'doc.ledger.code': (v: Vars) => `Ledger ${v['code']}`,
  'doc.ledger.ref': (v: Vars) => `Ledger ref. ${v['ref']} (not synced yet)`,

  // --- where it was read, and how --------------------------------------------
  'doc.route.readBy': (v: Vars) => `Read by ${v['model']}${v['host'] === '' ? '' : ` on ${v['host']}`}`,
  'doc.route.unrecordedModel': 'an unrecorded model',
  'doc.route.confirmed': 'region confirmed by the provider',
  'doc.route.notConfirmed': 'region NOT confirmed',
  'doc.details.summary': 'Source and reading details',
  'doc.details.direction': 'Direction',
  'doc.details.type': 'Type',
  'doc.details.notDetermined': 'Not determined',
  'doc.details.hash': 'Content hash',
  'doc.details.versions': 'Versions',
  'doc.details.noVersions': 'None stored',
  'doc.details.readAs': 'Read as',
  'doc.details.notRecorded': 'Not recorded',
  'doc.details.pages': 'Pages',
  'doc.details.unreadPages': (v: Vars) =>
    `page ${v['pages']} had no text and ${n(v) === 1 ? 'was' : 'were'} not read`,
  'doc.details.ocr': 'OCR confidence',
  'doc.details.modelRoute': 'Model route',
  'doc.details.converted': 'Converted',
  'doc.details.convertedIntro': 'Read in Dutch notation and converted before checking — compare with the paper:',
  'doc.details.dayFirst': '(read day first)',
  'doc.details.line': (v: Vars) => `Line ${v['number']} `,
  'doc.details.vatRow': (v: Vars) => `VAT row ${v['number']} `,
  'doc.details.regionCheck': 'Region check',
  'doc.details.lookups': (v: Vars) => `${n(v)} ${n(v) === 1 ? 'lookup' : 'lookups'}`,
  'doc.details.lastHttp': (v: Vars) => `, last HTTP ${v['status']}`,
  'doc.routeCheck.reported': 'Confirmed by the provider',
  'doc.routeCheck.not_found': 'Not confirmed — the provider had no record of where it ran in time; the answer was discarded',
  'doc.routeCheck.incomplete': 'Not confirmed — the provider’s record never named the region; the answer was discarded',
  'doc.routeCheck.http_error': 'Not confirmed — the provider refused the lookup; the answer was discarded',
  'doc.routeCheck.network': 'Not confirmed — the provider could not be reached for the lookup; the answer was discarded',
  'doc.routeCheck.not_json': 'Not confirmed — the provider’s lookup answer was unreadable; the answer was discarded',

  // --- a bank statement ------------------------------------------------------
  'doc.stmt.notImported': 'Not imported. The reason is below; no line from this file was stored.',
  'doc.stmt.lines': 'Lines',
  'doc.stmt.lineCounts': (v: Vars) => `${v['count']} in the file · ${v['new']} new`,
  'doc.stmt.alreadyStored': (v: Vars) => `${v['count']} already stored from an earlier file`,
  'doc.stmt.candidates': 'Match candidates',
  'doc.stmt.noneFound': 'None found',
  'doc.stmt.offered': (v: Vars) => `${v['count']} offered — review them in Bank matching`,
  'doc.stmt.openItems': 'Open items checked',
  'doc.stmt.notChecked': 'Not checked',
  'doc.stmt.account': 'Account',
  'doc.stmt.sequence': (v: Vars) => `statement ${v['sequence']}`,
  'doc.stmt.period': 'Period',
  'doc.stmt.balance': 'Balance',
  'doc.stmt.provedLines': (v: Vars) => `${v['count']} lines, proved to add up`,

  // --- approve and exclude ---------------------------------------------------
  'doc.actions.excluded': 'Excluded from the workflow. Nothing was deleted in the accounting package.',
  'doc.actions.explain':
    'Approving records your decision and queues one operation. Nothing is sent until the worker picks it up.',
  'doc.actions.exclude': 'Exclude…',
  'doc.actions.approve': 'Approve',
  'doc.actions.submitting': 'Submitting…',
  'doc.actions.reason': 'Reason',
  'doc.actions.reasonPlaceholder': 'Why this document is excluded',
  'doc.actions.evidence': 'Evidence reference',
  'doc.actions.evidencePlaceholder': 'e.g. document:INV-2026-0912',
  'doc.actions.excludeDocument': 'Exclude document',
  'doc.actions.noRevision': 'This proposal has no current revision to approve.',
  'doc.actions.failed': 'The request could not be completed.',
  'doc.actions.stale':
    'This changed since the screen was loaded, so nothing was submitted. The latest version is shown now.',
  'doc.actions.forbidden': 'Your role does not allow this action.',
  'doc.actions.absent': 'This is no longer available to your session.',
  'doc.actions.refused': (v: Vars) => `The request was refused: ${v['fields']}.`,
  'doc.actions.validation': 'validation failed',
  'doc.external.stillRunning':
    'Still not settled. The operation keeps running in the background — reopen this document to see where it got to.',

  // --- the processing history ------------------------------------------------
  'doc.history.title': 'Processing history',
  'doc.history.system': 'the system',
  'doc.history.you': 'you',
  'doc.history.colleague': 'a colleague',
  'doc.history.received': 'Document received',
  'doc.history.version': (v: Vars) => `Source version ${v['version']} stored`,
  'doc.history.proposal': 'proposal prepared',
  'doc.history.stopped': (v: Vars) => `stopped for a person (${n(v)} ${n(v) === 1 ? 'blocker' : 'blockers'})`,
  'doc.history.unreadable': 'could not be read',
  'doc.history.regionLate': 'Region not reported in time',
  'doc.history.regionConfirmed': 'Region confirmed — the earlier answer used, nothing sent again',
  'doc.history.readAgain': 'Read again',
  'doc.history.read': 'Read',
  'doc.history.settlementConfirmed': (v: Vars) => `Settlement confirmed, mutation ${v['mutation']}`,
  'doc.history.ruleSaved': (v: Vars) => `Supplier rule saved by ${v['who']}`,
  'doc.history.relation': (v: Vars) => `relation ${v['relation']}`,
  'doc.history.vatNotDeductible': 'VAT not deductible',
  'doc.history.classified': (v: Vars) => `Counterparty classified by ${v['who']}`,
  'doc.history.one_off': 'a one-off purchase',
  'doc.history.recurring_business': 'a recurring business supplier',
  'doc.history.by': (v: Vars) => `${v['title']} by ${v['who']}`,
  'doc.history.approve_submission': 'Approved',
  'doc.history.reject': 'Rejected',
  'doc.history.exclude': 'Excluded',
  'doc.history.verify_outcome': 'Outcome verified',
  'doc.history.approve_settlement': 'Settlement approved',
  'doc.history.decision': (v: Vars) => `Decision · ${v['kind']}`,
} satisfies Record<string, Message>;
